/**
 * Frontend port of backend design-tokens/parser.ts.
 * Parses DESIGN.md markdown into structured token categories.
 * Adds lineIndex tracking and updateTokenInMarkdown() for visual editing.
 */

export interface TokenEntry {
  name: string;
  value: string;
  comment?: string;
  /** 0-based line index in the original markdown — for precise replacement */
  lineIndex: number;
}

export interface DesignTokens {
  colors: {
    brand: TokenEntry[];
    semanticLight: TokenEntry[];
    semanticDark: TokenEntry[];
    status: TokenEntry[];
    roleAccents: TokenEntry[];
    adminCards: TokenEntry[];
  };
  typography: {
    fontStacks: TokenEntry[];
    typeScale: TokenEntry[];
    fontWeights: TokenEntry[];
  };
  spacing: TokenEntry[];
  borderRadius: TokenEntry[];
  shadows: TokenEntry[];
  transitions: TokenEntry[];
  layout: TokenEntry[];
  breakpoints: TokenEntry[];
  gradients: TokenEntry[];
}

/**
 * Parse a single code block line in the format: name: value  (with optional comment)
 */
function parseTokenLine(line: string): Omit<TokenEntry, 'lineIndex'> | null {
  const trimmed = line.trim();
  if (!trimmed || trimmed.startsWith('/*') || trimmed.startsWith('*')) return null;

  const match = trimmed.match(/^([\w-]+):\s*(.+?)(?:\s*\/\*\s*(.+?)\s*\*\/)?$/);
  if (!match) return null;

  return {
    name: match[1],
    value: match[2].trim(),
    comment: match[3]?.trim(),
  };
}

/**
 * Extract token entries from a markdown code block, tracking absolute line indices.
 */
function extractTokensFromBlock(content: string, startMarker: string): TokenEntry[] {
  const lines = content.split('\n');

  // Find the start marker line
  let markerLineIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].includes(startMarker)) {
      markerLineIdx = i;
      break;
    }
  }
  if (markerLineIdx === -1) return [];

  // Find the opening code fence after the marker
  let blockStartIdx = -1;
  for (let i = markerLineIdx + 1; i < lines.length; i++) {
    if (lines[i].trim().startsWith("\x60\x60\x60")) {
      blockStartIdx = i;
      break;
    }
  }
  if (blockStartIdx === -1) return [];

  // Find the closing code fence
  const tokens: TokenEntry[] = [];
  for (let i = blockStartIdx + 1; i < lines.length; i++) {
    if (lines[i].trim().startsWith("\x60\x60\x60")) break;
    const parsed = parseTokenLine(lines[i]);
    if (parsed) {
      tokens.push({ ...parsed, lineIndex: i });
    }
  }

  return tokens;
}

/**
 * Parse a DESIGN.md string into structured DesignTokens.
 */
export function parseDesignMd(designMd: string): DesignTokens {
  return {
    colors: {
      brand: extractTokensFromBlock(designMd, '### Brand Primitives'),
      semanticLight: extractTokensFromBlock(designMd, '### Semantic — Light Mode'),
      semanticDark: extractTokensFromBlock(designMd, '### Semantic — Dark Mode'),
      status: extractTokensFromBlock(designMd, '### Status Colors'),
      roleAccents: extractTokensFromBlock(designMd, '### Role Accents'),
      adminCards: extractTokensFromBlock(designMd, '### Admin Card Accents'),
    },
    typography: {
      fontStacks: extractTokensFromBlock(designMd, '### Font Stacks'),
      typeScale: extractTokensFromBlock(designMd, '### Type Scale'),
      fontWeights: extractTokensFromBlock(designMd, '### Font Weights'),
    },
    spacing: extractTokensFromBlock(designMd, '## Spacing'),
    borderRadius: extractTokensFromBlock(designMd, '## Border Radius'),
    shadows: extractTokensFromBlock(designMd, '## Shadows'),
    transitions: extractTokensFromBlock(designMd, '## Transitions'),
    layout: extractTokensFromBlock(designMd, '## Layout'),
    breakpoints: extractTokensFromBlock(designMd, '## Breakpoints'),
    gradients: extractTokensFromBlock(designMd, '## Gradients'),
  };
}

/**
 * Replace the value portion of a token line at the given lineIndex.
 * Preserves token name and comment.
 */
export function updateTokenInMarkdown(lineIndex: number, newValue: string, markdown: string): string {
  const lines = markdown.split('\n');
  if (lineIndex < 0 || lineIndex >= lines.length) return markdown;

  const line = lines[lineIndex];
  const match = line.match(/^(\s*)([\w-]+):\s*.+?(\s*\/\*.*\*\/)?$/);
  if (!match) return markdown;

  const indent = match[1];
  const name = match[2];
  const comment = match[3] || '';
  lines[lineIndex] = indent + name + ': ' + newValue + comment;

  return lines.join('\n');
}

/**
 * Returns true for valid hex color values (#rgb or #rrggbb).
 */
export function isHexColor(value: string): boolean {
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
}
